import React, { useState } from 'react';
import {
  Store,
  ShoppingCart,
  DollarSign,
  Package,
  Users,
  Award,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Archive,
  Send,
  Truck,
  X,
  Plus,
  Minus,
  Eye,
  Edit,
  Building,
  MapPin,
  ArrowLeft,
  Clock,
  ShoppingBag,
  AlertTriangle,
  BarChart3,
  Phone,
  Mail
} from 'lucide-react';

const CentralSystem = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreForTransfer, setSelectedStoreForTransfer] = useState(1);
  const [transferQuantity, setTransferQuantity] = useState(1);
  const [selectedProductForTransfer, setSelectedProductForTransfer] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Stock Central - Almacén general
  const [centralStock, setCentralStock] = useState([
    { 
      id: 1, 
      name: "Coca-Cola 500ml", 
      stock: 120, 
      price: 2.20, 
      category: "Bebidas", 
      barcode: "8410500001",
      alerts: []
    },
    { 
      id: 2, 
      name: "Pan Integral", 
      stock: 75, 
      price: 1.50, 
      category: "Panadería", 
      barcode: "8410500002",
      alerts: []
    },
    { 
      id: 3, 
      name: "Leche Entera 1L", 
      stock: 90, 
      price: 2.80, 
      category: "Lácteos", 
      barcode: "8410500003",
      alerts: []
    },
    { 
      id: 4, 
      name: "Aceite de Oliva", 
      stock: 8, 
      price: 4.50, 
      category: "Condimentos", 
      barcode: "8410500004",
      alerts: [{ type: "low_stock", message: "Stock crítico: Solo quedan 8 unidades", priority: "high" }]
    },
    { 
      id: 5, 
      name: "Arroz 1kg", 
      stock: 110, 
      price: 1.80, 
      category: "Granos", 
      barcode: "8410500005",
      alerts: []
    },
    { 
      id: 6, 
      name: "Galletas Oreo", 
      stock: 15, 
      price: 2.50, 
      category: "Snacks", 
      barcode: "8410500006",
      alerts: [{ type: "trending", message: "Producto en tendencia - Incrementar stock", priority: "medium" }]
    },
    { 
      id: 7, 
      name: "Atún en lata", 
      stock: 100, 
      price: 3.20, 
      category: "Conservas", 
      barcode: "8410500007",
      alerts: []
    },
    { 
      id: 8, 
      name: "Pasta Spaghetti", 
      stock: 125, 
      price: 1.60, 
      category: "Pasta", 
      barcode: "8410500008",
      alerts: []
    },
    { 
      id: 9, 
      name: "Jabón de manos", 
      stock: 5, 
      price: 1.90, 
      category: "Higiene", 
      barcode: "8410500009",
      alerts: [{ type: "critical_low", message: "Stock crítico: Solo 5 unidades disponibles", priority: "high" }]
    },
    { 
      id: 10, 
      name: "Agua Mineral 1L", 
      stock: 160, 
      price: 0.90, 
      category: "Bebidas", 
      barcode: "8410500010",
      alerts: []
    },
    { 
      id: 11, 
      name: "Yogurt Natural", 
      stock: 70, 
      price: 2.10, 
      category: "Lácteos", 
      barcode: "8410500011",
      alerts: []
    },
    { 
      id: 12, 
      name: "Cerveza Local", 
      stock: 90, 
      price: 3.40, 
      category: "Bebidas", 
      barcode: "8410500012",
      alerts: []
    },
    { 
      id: 13, 
      name: "Café Molido 250g", 
      stock: 45, 
      price: 3.95, 
      category: "Bebidas", 
      barcode: "8410500013",
      alerts: []
    },
    { 
      id: 14, 
      name: "Chocolate con Leche", 
      stock: 30, 
      price: 2.40, 
      category: "Snacks", 
      barcode: "8410500014",
      alerts: [{ type: "price_opportunity", message: "Oportunidad de ajustar precio - Competencia 8% más cara", priority: "medium" }]
    },
    { 
      id: 15, 
      name: "Papel Higiénico 6 rollos", 
      stock: 40, 
      price: 3.25, 
      category: "Higiene", 
      barcode: "8410500015",
      alerts: []
    },
  ]);

  // Lista de tiendas conectadas extendida
  // eslint-disable-next-line no-unused-vars
  const [stores, setStores] = useState([
    { 
      id: 1, 
      name: "Tienda Principal", 
      location: "Centro Comercial Vialia", 
      address: "Av. de Salamanca, 40, 37003 Salamanca",
      active: true,
      manager: "Carlos Ruiz",
      phone: "+34 923 456 789",
      email: "carlos.ruiz@tienda.com",
      stockItems: 45,
      lastOrder: new Date(2024, 6, 30),
      totalSales: 15670.50,
      monthlyTarget: 18000,
      orders: [
        { id: 1, products: ["Aceite de Oliva", "Jabón de manos"], quantity: 30, status: "pending", date: new Date() },
        { id: 2, products: ["Coca-Cola 500ml"], quantity: 50, status: "completed", date: new Date(2024, 6, 29) }
      ]
    },
    { 
      id: 2, 
      name: "Tienda Plaza Mayor", 
      location: "Plaza Mayor, 15", 
      address: "Plaza Mayor, 15, 37002 Salamanca",
      active: true,
      manager: "María González",
      phone: "+34 923 567 890",
      email: "maria.gonzalez@tienda.com",
      stockItems: 38,
      lastOrder: new Date(2024, 6, 29),
      totalSales: 12340.80,
      monthlyTarget: 15000,
      orders: [
        { id: 3, products: ["Pan Integral", "Leche Entera 1L"], quantity: 25, status: "approved", date: new Date(2024, 6, 28) }
      ]
    },
    { 
      id: 3, 
      name: "Tienda Estación Norte", 
      location: "Estación Norte, Local 3", 
      address: "Estación Norte, Local 3, 37004 Salamanca",
      active: true,
      manager: "Ana López",
      phone: "+34 923 678 901",
      email: "ana.lopez@tienda.com",
      stockItems: 52,
      lastOrder: new Date(2024, 6, 28),
      totalSales: 18920.30,
      monthlyTarget: 20000,
      orders: [
        { id: 4, products: ["Galletas Oreo", "Chocolate"], quantity: 40, status: "completed", date: new Date(2024, 6, 27) }
      ]
    },
    { 
      id: 4, 
      name: "Tienda Gran Vía", 
      location: "Gran Vía, 42", 
      address: "Gran Vía, 42, 37001 Salamanca",
      active: true,
      manager: "Pedro Martín",
      phone: "+34 923 789 012",
      email: "pedro.martin@tienda.com",
      stockItems: 41,
      lastOrder: new Date(2024, 6, 27),
      totalSales: 14560.90,
      monthlyTarget: 16000,
      orders: [
        { id: 5, products: ["Agua Mineral 1L"], quantity: 60, status: "pending", date: new Date() }
      ]
    },
    { 
      id: 5, 
      name: "Tienda Mercado Central", 
      location: "Mercado Central, Puesto 12", 
      address: "Mercado Central, Puesto 12, 37002 Salamanca",
      active: true,
      manager: "Isabel Sánchez",
      phone: "+34 923 890 123",
      email: "isabel.sanchez@tienda.com",
      stockItems: 35,
      lastOrder: new Date(2024, 6, 26),
      totalSales: 9870.45,
      monthlyTarget: 12000,
      orders: []
    },
    { 
      id: 6, 
      name: "Tienda El Tormes", 
      location: "C.C. El Tormes, Local 205", 
      address: "C.C. El Tormes, Local 205, 37003 Salamanca",
      active: true,
      manager: "Javier Hernández",
      phone: "+34 923 901 234",
      email: "javier.hernandez@tienda.com",
      stockItems: 48,
      lastOrder: new Date(2024, 6, 30),
      totalSales: 16780.20,
      monthlyTarget: 18000,
      orders: [
        { id: 6, products: ["Arroz 1kg", "Pasta Spaghetti"], quantity: 35, status: "approved", date: new Date(2024, 6, 30) }
      ]
    },
    { 
      id: 7, 
      name: "Tienda Barrio Oeste", 
      location: "Barrio del Oeste, Calle Nueva 8", 
      address: "Calle Nueva, 8, 37005 Salamanca",
      active: false,
      manager: "Laura Díaz",
      phone: "+34 923 012 345",
      email: "laura.diaz@tienda.com",
      stockItems: 0,
      lastOrder: new Date(2024, 6, 20),
      totalSales: 5670.30,
      monthlyTarget: 8000,
      orders: []
    },
    { 
      id: 8, 
      name: "Tienda Carmelitas", 
      location: "Paseo Carmelitas, 28", 
      address: "Paseo Carmelitas, 28, 37002 Salamanca",
      active: true,
      manager: "Roberto García",
      phone: "+34 923 123 456",
      email: "roberto.garcia@tienda.com",
      stockItems: 39,
      lastOrder: new Date(2024, 6, 29),
      totalSales: 11450.60,
      monthlyTarget: 14000,
      orders: [
        { id: 7, products: ["Cerveza Local", "Atún en lata"], quantity: 20, status: "completed", date: new Date(2024, 6, 29) }
      ]
    }
  ]);

  // Base de datos simulada - Sistema Central
  const centralData = {
    totalStores: stores.filter(store => store.active).length,
    totalSales: 245,
    totalRevenue: stores.reduce((sum, store) => sum + store.totalSales, 0),
    activeProducts: centralStock.length,
  };

  // Productos más vendidos simulados
  const topProducts = [
    { id: 1, name: "Coca-Cola 500ml", totalSold: 45, price: 2.50 },
    { id: 2, name: "Pan Integral", totalSold: 38, price: 1.80 },
    { id: 3, name: "Leche Entera 1L", totalSold: 35, price: 3.20 },
    { id: 4, name: "Agua Mineral 1L", totalSold: 32, price: 1.20 },
    { id: 5, name: "Arroz 1kg", totalSold: 28, price: 2.10 },
  ];

  // Transferir stock del sistema central a la tienda
  const transferStockToStore = () => {
    if (!selectedProductForTransfer || transferQuantity <= 0) return;
    
    const centralProduct = centralStock.find(p => p.id === selectedProductForTransfer.id);
    if (!centralProduct || centralProduct.stock < transferQuantity) {
      alert("Stock insuficiente en el almacén central");
      return;
    }
    
    setCentralStock(prev => prev.map(p => 
      p.id === selectedProductForTransfer.id 
        ? { ...p, stock: p.stock - transferQuantity } 
        : p
    ));
    
    setShowTransferModal(false);
    setSelectedProductForTransfer(null);
    setTransferQuantity(1);
    
    alert(`${transferQuantity} unidades de ${selectedProductForTransfer.name} transferidas a la tienda`);
  };

  // Obtener icono de alerta
  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case "low_stock":
      case "critical_low":
        return AlertTriangle;
      case "trending":
        return TrendingUp;
      case "price_opportunity":
        return DollarSign;
      default:
        return AlertCircle;
    }
  };

  // Obtener color de alerta
  const getAlertColor = (priority) => {
    switch (priority) {
      case "high":
        return "#EF4444";
      case "medium":
        return "#F59E0B";
      case "low":
        return "#3B82F6";
      default:
        return "#6B7280";
    }
  };

  // Función para volver al dashboard
  const goToDashboard = () => {
    setActiveView('dashboard');
    setSelectedStore(null);
  };

  // Función para volver a stores desde store-detail
  const goToStores = () => {
    setActiveView('stores');
    setSelectedStore(null);
  };

  // Calcular progreso de objetivos
  const calculateProgress = (sales, target) => {
    return Math.min((sales / target) * 100, 100);
  };

  const renderStoreDetail = (store) => (
    <div className="store-detail-view">
      <div className="store-detail-grid">

        {/* Información de contacto mejorada */}
        <div className="panel contact-panel">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Users className="panel-icon" />
            </div>
            <h3>Información de Contacto</h3>
          </div>
          <div className="contact-info-enhanced">
            <div className="contact-item">
              <div className="contact-icon">
                <Users size={18} />
              </div>
              <div className="contact-details">
                <span className="contact-label">Gerente</span>
                <span className="contact-value">{store.manager}</span>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">
                <Phone size={18} />
              </div>
              <div className="contact-details">
                <span className="contact-label">Teléfono</span>
                <span className="contact-value">{store.phone}</span>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">
                <Mail size={18} />
              </div>
              <div className="contact-details">
                <span className="contact-label">Email</span>
                <span className="contact-value">{store.email}</span>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">
                <MapPin size={18} />
              </div>
              <div className="contact-details">
                <span className="contact-label">Dirección</span>
                <span className="contact-value">{store.address}</span>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">
                <Clock size={18} />
              </div>
              <div className="contact-details">
                <span className="contact-label">Último Pedido</span>
                <span className="contact-value">{store.lastOrder.toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Últimos pedidos mejorado */}
        <div className="panel orders-panel">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <ShoppingBag className="panel-icon" />
            </div>
            <h3>Pedidos Recientes</h3>
            <button className="add-order-button">
              <Plus size={16} />
              <span>Nuevo Pedido</span>
            </button>
          </div>
          <div className="orders-list-enhanced">
            {store.orders.length > 0 ? (
              store.orders.map(order => (
                <div key={order.id} className={`order-item-enhanced ${order.status}`}>
                  <div className="order-main-info">
                    <div className="order-header">
                      <div className="order-id">#{order.id.toString().padStart(4, '0')}</div>
                      <div className={`order-status-badge ${order.status}`}>
                        {order.status === 'pending' && <Clock size={14} />}
                        {order.status === 'approved' && <CheckCircle size={14} />}
                        {order.status === 'completed' && <Package size={14} />}
                        <span>
                          {order.status === 'pending' && 'Pendiente'}
                          {order.status === 'approved' && 'Aprobado'}
                          {order.status === 'completed' && 'Completado'}
                        </span>
                      </div>
                    </div>
                    <div className="order-products">{order.products.join(", ")}</div>
                    <div className="order-meta">
                      <span className="order-quantity">{order.quantity} unidades</span>
                      <span className="order-date">{order.date.toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="order-actions">
                    <button className="order-action-btn view">
                      <Eye size={14} />
                    </button>
                    <button className="order-action-btn edit">
                      <Edit size={14} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-orders-enhanced">
                <div className="empty-icon">
                  <ShoppingBag size={48} />
                </div>
                <h4>No hay pedidos recientes</h4>
                <p>Esta tienda no ha realizado pedidos recientemente</p>
                <button className="create-order-button">
                  <Plus size={16} />
                  <span>Crear Primer Pedido</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Análisis de rendimiento */}
        <div className="panel performance-panel full-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <BarChart3 className="panel-icon" />
            </div>
            <h3>Análisis de Rendimiento</h3>
          </div>
          <div className="performance-grid">
            <div className="performance-metric">
              <div className="metric-header">
                <h4>Ventas vs Objetivo</h4>
                <span className="metric-value">${store.totalSales.toLocaleString()} / ${store.monthlyTarget.toLocaleString()}</span>
              </div>
              <div className="metric-progress">
                <div className="progress-bar large">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${calculateProgress(store.totalSales, store.monthlyTarget)}%` }}
                  ></div>
                </div>
                <span className="progress-percentage">
                  {Math.round(calculateProgress(store.totalSales, store.monthlyTarget))}% completado
                </span>
              </div>
            </div>
            
            <div className="performance-insights">
              <h4>Insights</h4>
              <div className="insights-list">
                <div className="insight-item positive">
                  <TrendingUp size={16} />
                  <span>Ventas 15% por encima del promedio</span>
                </div>
                <div className="insight-item neutral">
                  <Package size={16} />
                  <span>Stock en nivel óptimo</span>
                </div>
                {store.orders.length > 0 && (
                  <div className="insight-item warning">
                    <AlertCircle size={16} />
                    <span>{store.orders.filter(o => o.status === 'pending').length} pedidos pendientes</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStoresView = () => (
    <div className="stores-view">
      <div className="stores-header">
        <h3>Red de Tiendas Conectadas</h3>
        <button className="add-store-button">
          <Plus size={16} />
          <span>Añadir Tienda</span>
        </button>
      </div>
      <div className="stores-grid-detailed">
        {stores.map(store => (
          <div key={store.id} className="store-card-detailed">
            <div className="store-card-header">
              <div className="store-avatar">
                <Store size={24} />
              </div>
              <div className="store-info">
                <h4>{store.name}</h4>
                <p>{store.location}</p>
              </div>
              <div className={`connection-status ${store.active ? 'active' : 'inactive'}`}>
                <div className="status-dot"></div>
                <span>{store.active ? 'Activa' : 'Inactiva'}</span>
              </div>
            </div>
            <div className="store-metrics">
              <div className="metric">
                <span className="metric-value">{store.stockItems}</span>
                <span className="metric-label">Productos</span>
              </div>
              <div className="metric">
                <span className="metric-value">${(store.totalSales/1000).toFixed(0)}k</span>
                <span className="metric-label">Ventas</span>
              </div>
              <div className="metric">
                <span className="metric-value">{store.orders.length}</span>
                <span className="metric-label">Pedidos</span>
              </div>
            </div>
            <div className="store-details">
              <div className="detail-row">
                <span>Gerente: {store.manager}</span>
              </div>
              <div className="detail-row">
                <span>Último pedido: {store.lastOrder.toLocaleDateString()}</span>
              </div>
              <div className="detail-row progress-row">
                <span>Objetivo mensual</span>
                <div className="mini-progress">
                  <div 
                    className="mini-progress-fill" 
                    style={{ width: `${calculateProgress(store.totalSales, store.monthlyTarget)}%` }}
                  ></div>
                </div>
                <span>{Math.round(calculateProgress(store.totalSales, store.monthlyTarget))}%</span>
              </div>
            </div>
            <div className="store-actions">
              <button 
                className="view-store-button"
                onClick={() => {setSelectedStore(store); setActiveView('store-detail');}}
              >
                <Eye size={14} />
                <span>Ver Detalle</span>
              </button>
              <button className="edit-store-button">
                <Edit size={14} />
                <span>Editar</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="central-view">
      <div className="central-grid">
        {/* KPIs Principales */}
        <div className="kpi-panel">
          <div className="kpi-grid">
            <div className="kpi-card primary">
              <div className="kpi-icon">
                <Store />
              </div>
              <div className="kpi-content">
                <h3>{centralData.totalStores}</h3>
                <p>Tiendas Conectadas</p>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="kpi-icon">
                <ShoppingCart />
              </div>
              <div className="kpi-content">
                <h3>{centralData.totalSales}</h3>
                <p>Ventas Totales</p>
              </div>
            </div>
            <div className="kpi-card warning">
              <div className="kpi-icon">
                <DollarSign />
              </div>
              <div className="kpi-content">
                <h3>${centralData.totalRevenue.toFixed(0)}k</h3>
                <p>Ingresos Totales</p>
              </div>
            </div>
            <div className="kpi-card info">
              <div className="kpi-icon">
                <Package />
              </div>
              <div className="kpi-content">
                <h3>{centralData.activeProducts}</h3>
                <p>Productos Activos</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stock Central con Alertas */}
        <div className="panel full-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Archive className="panel-icon" />
            </div>
            <h3>Stock Central - Almacén General</h3>
          </div>
          <div className="central-stock-list">
            <div className="stock-list-header">
              <span className="product-id">ID</span>
              <span className="product-name">Producto</span>
              <span className="product-category">Categoría</span>
              <span className="product-stock">Stock</span>
              <span className="product-price">Precio</span>
              <span className="product-actions">Acciones</span>
            </div>
            {centralStock.map((product) => (
              <div key={product.id} className="stock-list-item">
                <span className="product-id">{product.id}</span>
                <span className="product-name">{product.name}</span>
                <span className="product-category">{product.category}</span>
                <span className="product-stock-with-alert">
                  <span className="stock-value">{product.stock}</span>
                  {product.alerts.length > 0 && (
                    <div className="alert-indicators">
                      {product.alerts.map((alert, index) => {
                        const AlertIcon = getAlertIcon(alert.type);
                        return (
                          <div 
                            key={index}
                            className="alert-icon-wrapper"
                            title={alert.message}
                            style={{ color: getAlertColor(alert.priority) }}
                          >
                            <AlertIcon size={16} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </span>
                <span className="product-price">${product.price.toFixed(2)}</span>
                <span className="product-actions">
                  <button 
                    className="transfer-button"
                    onClick={() => {
                      setSelectedProductForTransfer(product);
                      setShowTransferModal(true);
                    }}
                  >
                    <Send size={14} />
                    <span>Transferir</span>
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Red de Tiendas */}
        <div className="panel half-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Users className="panel-icon" />
            </div>
            <h3>Red de Tiendas</h3>
            <button 
              className="view-all-stores-button"
              onClick={() => setActiveView('stores')}
            >
              <Eye size={14} />
              <span>Ver Todas</span>
            </button>
          </div>
          <div className="stores-grid">
            {stores.slice(0, 6).map((store) => (
              <div 
                key={store.id} 
                className={`store-node ${store.id === 1 ? "active" : store.active ? "connected" : "inactive"}`}
                onClick={() => {setSelectedStore(store); setActiveView('store-detail');}}
              >
                <div className="store-avatar">
                  <Store size={18} />
                </div>
                <div className="store-info">
                  <div className="store-name">{store.name}</div>
                  <div className="store-status">
                    {store.active ? store.location : "Inactiva"}
                  </div>
                </div>
                <div className={`connection-dot ${store.active ? "connected" : "inactive"}`}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Productos Top */}
        <div className="panel half-width">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Award className="panel-icon" />
            </div>
            <h3>Productos Más Vendidos</h3>
          </div>
          <div className="products-ranking">
            {topProducts.map((product, index) => (
              <div key={product.id} className="ranking-item">
                <div className="rank-number">{index + 1}</div>
                <div className="product-details">
                  <div className="product-name">{product.name}</div>
                  <div className="product-stats">
                    <span className="sales-count">{product.totalSold} vendidos</span>
                    <span className="revenue">${(product.totalSold * product.price).toFixed(2)}</span>
                  </div>
                </div>
                <div className="trend-indicator">
                  <TrendingUp size={16} className="trend-up" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Función para renderizar el header según la vista
  const renderHeader = () => {
    const baseHeader = (
      <div className="header-info">
        <h2>Sistema Central - Dashboard</h2>
        <p>Monitoreo global de todas las sucursales</p>
      </div>
    );

    const storesHeader = (
      <div className="header-info">
        <div className="header-navigation">
          <button className="back-nav-button" onClick={goToDashboard}>
            <ArrowLeft size={16} />
            <span>Dashboard</span>
          </button>
          <div className="header-divider">›</div>
          <span className="current-page">Gestión de Tiendas</span>
        </div>
        <p>Administra tu red de tiendas conectadas</p>
      </div>
    );

    const storeDetailHeader = selectedStore ? (
      <div className="header-info">
        <div className="header-navigation">
          <button className="back-nav-button" onClick={goToDashboard}>
            <ArrowLeft size={16} />
            <span>Dashboard</span>
          </button>
          <div className="header-divider">›</div>
          <button className="back-nav-button" onClick={goToStores}>
            <span>Tiendas</span>
          </button>
          <div className="header-divider">›</div>
          <span className="current-page">{selectedStore.name}</span>
        </div>
        <p>Información detallada de la tienda</p>
      </div>
    ) : baseHeader;

    return (
      <header className="content-header">
        {activeView === 'dashboard' && baseHeader}
        {activeView === 'stores' && storesHeader}
        {activeView === 'store-detail' && storeDetailHeader}
        
        <div className="header-actions">
          {activeView === 'dashboard' && (
            <button 
              className="stores-button"
              onClick={() => setActiveView('stores')}
            >
              <Building size={16} />
              <span>Gestionar Tiendas</span>
            </button>
          )}
          <div className="header-metrics">
            <div className="metric-item">
              <div className="metric-icon active">
                <Truck />
              </div>
              <div>
                <strong>2</strong>
                <span>En Ruta</span>
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-icon available">
                <CheckCircle />
              </div>
              <div>
                <strong>6</strong>
                <span>Disponibles</span>
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  };

  const renderContent = () => {
    if (activeView === 'store-detail' && selectedStore) {
      return renderStoreDetail(selectedStore);
    }
    if (activeView === 'stores') {
      return renderStoresView();
    }
    return renderDashboard();
  };

  return (
    <div className="central-system">
      {renderHeader()}
      {renderContent()}

      {/* Modal de Transferencia de Stock */}
      {showTransferModal && selectedProductForTransfer && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Transferir Stock a Tienda</h3>
              <button 
                className="modal-close-button"
                onClick={() => setShowTransferModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-content">
              <div className="product-info-row">
                <span className="info-label">Producto:</span>
                <span className="info-value">{selectedProductForTransfer.name}</span>
              </div>
              <div className="product-info-row">
                <span className="info-label">Stock Disponible:</span>
                <span className="info-value">{selectedProductForTransfer.stock} unidades</span>
              </div>
              <div className="form-row">
                <label>Tienda Destino:</label>
                <select 
                  value={selectedStoreForTransfer}
                  onChange={(e) => setSelectedStoreForTransfer(parseInt(e.target.value))}
                >
                  {stores.filter(store => store.active).map(store => (
                    <option key={store.id} value={store.id}>
                      {store.name} - {store.location}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Cantidad a Transferir:</label>
                <div className="quantity-controls">
                  <button 
                    onClick={() => setTransferQuantity(prev => Math.max(1, prev - 1))}
                    className="qty-btn"
                  >
                    <Minus size={14} />
                  </button>
                  <input 
                    type="number" 
                    min="1" 
                    max={selectedProductForTransfer.stock}
                    value={transferQuantity} 
                    onChange={(e) => setTransferQuantity(parseInt(e.target.value) || 1)}
                  />
                  <button 
                    onClick={() => setTransferQuantity(prev => Math.min(selectedProductForTransfer.stock, prev + 1))}
                    className="qty-btn"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowTransferModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="transfer-stock-button"
                onClick={transferStockToStore}
              >
                <Send size={16} />
                <span>Transferir Stock</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .central-system {
          padding: 24px;
          background: #f9fafb;
          min-height: 100vh;
        }

        .content-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .header-info h2 {
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .header-info p {
          color: #6b7280;
          margin: 0;
        }

        /* Header Navigation */
        .header-navigation {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .back-nav-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: transparent;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          color: #6b7280;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-nav-button:hover {
          background: #f3f4f6;
          color: #374151;
          border-color: #d1d5db;
        }

        .header-divider {
          color: #9ca3af;
          font-weight: 500;
        }

        .current-page {
          color: #1f2937;
          font-weight: 600;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .stores-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .stores-button:hover {
          background: #2563eb;
          transform: translateY(-1px);
        }

        .header-metrics {
          display: flex;
          gap: 16px;
        }

        .metric-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        .metric-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metric-icon.active {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .metric-icon.available {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .metric-item strong {
          display: block;
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
        }

        .metric-item span {
          font-size: 12px;
          color: #6b7280;
        }

        .central-view {
          max-width: 1600px;
          margin: 0 auto;
        }

        .central-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
        }

        .kpi-panel {
          grid-column: 1 / -1;
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .kpi-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          border-radius: 12px;
          transition: all 0.3s ease;
          color: white;
        }

        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .kpi-card.primary {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .kpi-card.success {
          background: linear-gradient(135deg, #10b981, #059669);
        }

        .kpi-card.warning {
          background: linear-gradient(135deg, #f59e0b, #d97706);
        }

        .kpi-card.info {
          background: linear-gradient(135deg, #6366f1, #7c3aed);
        }

        .kpi-icon {
          width: 48px;
          height: 48px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .kpi-content h3 {
          margin: 0 0 4px;
          font-size: 28px;
          font-weight: 700;
        }

        .kpi-content p {
          margin: 0 0 8px;
          opacity: 0.9;
        }

        .kpi-trend {
          font-size: 12px;
          padding: 2px 8px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.2);
        }

        .panel {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          margin-bottom: 20px;
        }

        .full-width {
          grid-column: 1 / -1;
        }

        .half-width {
          grid-column: span 1;
        }

        .panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .panel-header > div:first-child {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .panel-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: #3b82f6;
          border-radius: 8px;
        }

        .panel-icon {
          width: 18px;
          height: 18px;
          color: white;
        }

        .panel-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .view-all-stores-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 6px;
          background: #3b82f6;
          color: white;
          border: none;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .view-all-stores-button:hover {
          background: #2563eb;
        }

        /* Central Stock List con Alertas */
        .central-stock-list {
          padding: 20px;
          max-height: 600px;
          overflow-y: auto;
        }

        .stock-list-header {
          display: grid;
          grid-template-columns: 60px 2fr 1fr 1fr 1fr 1fr;
          padding: 12px 16px;
          font-weight: 600;
          color: #6b7280;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 12px;
        }

        .stock-list-item {
          display: grid;
          grid-template-columns: 60px 2fr 1fr 1fr 1fr 1fr;
          padding: 12px 16px;
          align-items: center;
          border-radius: 8px;
          background: #f9fafb;
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }

        .stock-list-item:hover {
          background: #eff6ff;
        }

        .product-id {
          font-weight: 600;
          color: #6b7280;
        }

        .product-name {
          font-weight: 600;
          color: #1f2937;
        }

        .product-category {
          color: #6b7280;
        }

        .product-stock-with-alert {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .stock-value {
          font-weight: 600;
          color: #3b82f6;
        }

        .alert-indicators {
          display: flex;
          gap: 4px;
        }

        .alert-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .alert-icon-wrapper:hover {
          transform: scale(1.2);
          background: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .product-price {
          font-weight: 600;
          color: #1f2937;
        }

        .product-actions {
          display: flex;
          justify-content: flex-end;
        }

        .transfer-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 6px;
          background: #3b82f6;
          color: white;
          border: none;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .transfer-button:hover {
          background: #2563eb;
        }

        /* Stores Grid */
        .stores-grid {
          padding: 20px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }

        .store-node {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .store-node:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
        }

        .store-node.active {
          border-color: #3b82f6;
          background: rgba(59, 130, 246, 0.05);
        }

        .store-node.inactive {
          opacity: 0.6;
          background: #f3f4f6;
        }

        .store-avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: #3b82f6;
          border-radius: 8px;
          color: white;
        }

        .store-node.inactive .store-avatar {
          background: #9ca3af;
        }

        .store-info {
          flex: 1;
        }

        .store-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 2px;
        }

        .store-status {
          font-size: 12px;
          color: #6b7280;
        }

        .connection-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
        }

        .connection-dot.connected {
          animation: pulse 2s infinite;
        }

        .connection-dot.inactive {
          background: #ef4444;
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.5; }
          100% { transform: scale(1); opacity: 1; }
        }

        /* Products Ranking */
        .products-ranking {
          padding: 20px;
        }

        .ranking-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 8px;
          border: 1px solid #e5e7eb;
        }

        .rank-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: #3b82f6;
          color: white;
          border-radius: 8px;
          font-weight: 700;
        }

        .product-details {
          flex: 1;
        }

        .product-name {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .product-stats {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: #6b7280;
        }

        .revenue {
          font-weight: 600;
          color: #3b82f6;
        }

        .trend-indicator {
          color: #10b981;
        }

        /* System Alerts */
        .alerts-list {
          padding: 20px;
        }

        .alert-item {
          display: flex;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 8px;
          border-left: 4px solid;
        }

        .alert-item.high {
          background: rgba(239, 68, 68, 0.05);
          border-left-color: #ef4444;
        }

        .alert-item.medium {
          background: rgba(245, 158, 11, 0.05);
          border-left-color: #f59e0b;
        }

        .alert-item.low {
          background: rgba(16, 185, 129, 0.05);
          border-left-color: #10b981;
        }

        .alert-icon {
          width: 20px;
          height: 20px;
          margin-top: 2px;
        }

        .alert-item.high .alert-icon {
          color: #ef4444;
        }

        .alert-item.medium .alert-icon {
          color: #f59e0b;
        }

        .alert-item.low .alert-icon {
          color: #10b981;
        }

        .alert-content {
          flex: 1;
        }

        .alert-title {
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .alert-message {
          color: #6b7280;
          font-size: 14px;
        }

        /* Stores View */
        .stores-view {
          max-width: 1600px;
          margin: 0 auto;
        }

        .stores-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .stores-header h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .add-store-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-store-button:hover {
          background: #2563eb;
        }

        .stores-grid-detailed {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 20px;
        }

        .store-card-detailed {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .store-card-detailed:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .store-card-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          border-bottom: 1px solid #f3f4f6;
        }

        .store-card-detailed .store-avatar {
          width: 48px;
          height: 48px;
          background: #3b82f6;
          border-radius: 12px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .store-card-detailed .store-info {
          flex: 1;
        }

        .store-card-detailed .store-info h4 {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
        }

        .store-card-detailed .store-info p {
          font-size: 14px;
          color: #6b7280;
          margin: 0;
        }

        .connection-status {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .connection-status.active {
          color: #10b981;
        }

        .connection-status.inactive {
          color: #ef4444;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .connection-status.active .status-dot {
          background: #10b981;
        }

        .connection-status.inactive .status-dot {
          background: #ef4444;
        }

        .store-metrics {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          padding: 16px 20px;
          border-bottom: 1px solid #f3f4f6;
        }

        .metric {
          text-align: center;
        }

        .metric-value {
          display: block;
          font-size: 18px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .metric-label {
          font-size: 12px;
          color: #6b7280;
        }

        .store-details {
          padding: 16px 20px;
          border-bottom: 1px solid #f3f4f6;
        }

        .detail-row {
          margin-bottom: 8px;
          font-size: 14px;
          color: #6b7280;
        }

        .progress-row {
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: space-between;
        }

        .mini-progress {
          flex: 1;
          height: 4px;
          background: #e5e7eb;
          border-radius: 2px;
          overflow: hidden;
        }

        .mini-progress-fill {
          height: 100%;
          background: #3b82f6;
          transition: width 0.3s ease;
        }

        .store-actions {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
        }

        .view-store-button, .edit-store-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          flex: 1;
          justify-content: center;
        }

        .view-store-button {
          background: #3b82f6;
          color: white;
          border: none;
        }

        .view-store-button:hover {
          background: #2563eb;
        }

        .edit-store-button {
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #e5e7eb;
        }

        .edit-store-button:hover {
          background: white;
          color: #374151;
        }

        /* Store Detail View Mejorada */
        .store-detail-view {
          max-width: 1600px;
          margin: 0 auto;
        }

        .store-detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
        }

        /* Store Overview Card */
        .store-overview-card {
          grid-column: 1 / -1;
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .store-hero {
          display: flex;
          align-items: center;
          gap: 24px;
          padding: 24px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .store-avatar-large {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 16px;
          backdrop-filter: blur(10px);
        }

        .store-main-info {
          flex: 1;
        }

        .store-main-info h2 {
          margin: 0 0 8px 0;
          font-size: 28px;
          font-weight: 700;
        }

        .store-location {
          font-size: 16px;
          margin: 0 0 4px 0;
          opacity: 0.9;
        }

        .store-address {
          font-size: 14px;
          margin: 0 0 12px 0;
          opacity: 0.7;
        }

        .store-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
        }

        .store-status-badge.active {
          color: #10b981;
        }

        .store-status-badge.inactive {
          color: #ef4444;
        }

        .store-performance {
          display: flex;
          align-items: center;
        }

        .performance-circle {
          position: relative;
        }

        .circle-progress {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .circle-inner {
          width: 70px;
          height: 70px;
          background: white;
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .percentage {
          font-size: 18px;
          font-weight: 700;
          color: #3b82f6;
        }

        .label {
          font-size: 10px;
          color: #6b7280;
        }

        /* Store KPIs Grid */
        .store-kpis-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          padding: 24px;
        }

        .store-kpi-card {
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .store-kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .store-kpi-card.sales {
          background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
          border-color: #3b82f6;
        }

        .store-kpi-card.stock {
          background: linear-gradient(135deg, #f0fdf4, #dcfce7);
          border-color: #10b981;
        }

        .store-kpi-card.orders {
          background: linear-gradient(135deg, #fffbeb, #fef3c7);
          border-color: #f59e0b;
        }

        .store-kpi-card.target {
          background: linear-gradient(135deg, #faf5ff, #f3e8ff);
          border-color: #8b5cf6;
        }

        .store-kpi-card .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .store-kpi-card .kpi-icon.success {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .store-kpi-card .kpi-icon.primary {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .store-kpi-card .kpi-icon.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .store-kpi-card .kpi-icon.info {
          background: rgba(139, 92, 246, 0.1);
          color: #8b5cf6;
        }

        .store-kpi-card .kpi-content h3 {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 4px 0;
        }

        .store-kpi-card .kpi-content p {
          color: #6b7280;
          margin: 0 0 12px 0;
          font-size: 14px;
        }

        .kpi-trend {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 16px;
        }

        .kpi-trend.positive {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .kpi-trend.negative {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .kpi-trend.neutral {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
        }

        .kpi-progress {
          margin-top: 8px;
        }

        .progress-bar {
          height: 6px;
          background: #e5e7eb;
          border-radius: 3px;
          overflow: hidden;
          margin-bottom: 4px;
        }

        .progress-bar.large {
          height: 8px;
        }

        .progress-fill {
          height: 100%;
          background: #3b82f6;
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 11px;
          color: #6b7280;
        }

        .progress-percentage {
          font-size: 12px;
          color: #6b7280;
        }

        /* Contact Panel Enhanced */
        .contact-panel {
          height: fit-content;
        }

        .contact-info-enhanced {
          padding: 20px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .contact-item:last-child {
          border-bottom: none;
        }

        .contact-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: #f3f4f6;
          border-radius: 8px;
          color: #6b7280;
        }

        .contact-details {
          flex: 1;
        }

        .contact-label {
          display: block;
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 2px;
        }

        .contact-value {
          font-weight: 600;
          color: #1f2937;
        }

        /* Orders Panel Enhanced */
        .orders-panel {
          height: fit-content;
        }

        .add-order-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-order-button:hover {
          background: #059669;
        }

        .orders-list-enhanced {
          padding: 20px;
          max-height: 500px;
          overflow-y: auto;
        }

        .order-item-enhanced {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 12px;
          transition: all 0.3s ease;
        }

        .order-item-enhanced:hover {
          background: #f9fafb;
          transform: translateY(-1px);
        }

        .order-item-enhanced.pending {
          border-left: 4px solid #f59e0b;
        }

        .order-item-enhanced.approved {
          border-left: 4px solid #3b82f6;
        }

        .order-item-enhanced.completed {
          border-left: 4px solid #10b981;
        }

        .order-main-info {
          flex: 1;
        }

        .order-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .order-id {
          font-weight: 700;
          color: #1f2937;
        }

        .order-status-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 16px;
          font-size: 11px;
          font-weight: 500;
        }

        .order-status-badge.pending {
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
        }

        .order-status-badge.approved {
          background: rgba(59, 130, 246, 0.1);
          color: #2563eb;
        }

        .order-status-badge.completed {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
        }

        .order-products {
          font-size: 14px;
          color: #374151;
          margin-bottom: 4px;
        }

        .order-meta {
          display: flex;
          gap: 16px;
          font-size: 12px;
          color: #6b7280;
        }

        .order-actions {
          display: flex;
          gap: 8px;
        }

        .order-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .order-action-btn.view {
          color: #3b82f6;
        }

        .order-action-btn.edit {
          color: #f59e0b;
        }

        .order-action-btn:hover {
          background: white;
          transform: translateY(-1px);
        }

        .empty-orders-enhanced {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 40px 20px;
          text-align: center;
        }

        .empty-icon {
          color: #d1d5db;
        }

        .empty-orders-enhanced h4 {
          margin: 0;
          color: #6b7280;
        }

        .empty-orders-enhanced p {
          margin: 0;
          color: #9ca3af;
        }

        .create-order-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .create-order-button:hover {
          background: #2563eb;
        }

        /* Performance Panel */
        .performance-panel {
          height: fit-content;
        }

        .performance-grid {
          padding: 20px;
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 20px;
        }

        .performance-metric {
          background: #f9fafb;
          padding: 16px;
          border-radius: 8px;
        }

        .metric-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .metric-header h4 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .metric-value {
          font-size: 12px;
          color: #6b7280;
        }

        .metric-progress {
          margin-top: 8px;
        }

        .performance-insights h4 {
          margin: 0 0 12px 0;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .insights-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .insight-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }

        .insight-item.positive {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
        }

        .insight-item.neutral {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;
        }

        .insight-item.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
        }

        .modal-container {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 500px;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .modal-close-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .modal-close-button:hover {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }

        .modal-content {
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .product-info-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          padding: 8px 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .info-label {
          font-weight: 600;
          color: #6b7280;
        }

        .info-value {
          font-weight: 600;
          color: #1f2937;
        }

        .form-row {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-row label {
          font-weight: 600;
          color: #1f2937;
        }

        .form-row select {
          padding: 10px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          font-size: 14px;
          background: #f9fafb;
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .qty-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #3b82f6;
        }

        .qty-btn:hover {
          background: rgba(59, 130, 246, 0.2);
        }

        .quantity-controls input {
          width: 60px;
          text-align: center;
          padding: 8px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          font-size: 14px;
        }

        .modal-footer {
          padding: 16px 20px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .cancel-button {
          padding: 10px 16px;
          border-radius: 8px;
          background: #f9fafb;
          color: #6b7280;
          border: 1px solid #e5e7eb;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-button:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border-color: #ef4444;
        }

        .transfer-stock-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .transfer-stock-button:hover {
          background: #2563eb;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .central-grid {
            grid-template-columns: 1fr;
          }
          
          .store-kpis-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .performance-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 1024px) {
          .stock-list-header, .stock-list-item {
            grid-template-columns: 60px 2fr 1fr 1fr 1fr;
          }
          
          .product-actions {
            display: none;
          }

          .store-hero {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }

          .stores-grid-detailed {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .content-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .header-actions {
            justify-content: space-between;
          }

          .header-navigation {
            flex-wrap: wrap;
            gap: 8px;
          }

          .kpi-grid {
            grid-template-columns: 1fr;
          }

          .store-kpis-grid {
            grid-template-columns: 1fr;
          }

          .stock-list-header, .stock-list-item {
            grid-template-columns: 60px 2fr 1fr;
          }
          
          .product-category, .product-price {
            display: none;
          }
        }

        @media (max-width: 576px) {
          .store-hero {
            padding: 16px;
          }

          .performance-circle {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default CentralSystem;